import type { Router } from 'vue-router'
import { authService } from '@/services/authService'
import { logger } from '@/utils/logger'

/**
 * 設定路由守衛
 * 基於 MENU_CONFIG 進行權限驗證，確保選單權限與路由權限一致
 */
export function setupRouterGuards(router: Router) {
  router.beforeEach((to, from, next) => {
    const targetPath = to.path

    // 登入相關路由、註冊路由和 Sample 頁面允許所有使用者訪問
    if (targetPath.startsWith('/login') || 
        targetPath.startsWith('/admin/login') ||
        targetPath.startsWith('/register') ||
        targetPath.startsWith('/admin/register') ||
        targetPath.startsWith('/mock-user-selection') ||
        targetPath.startsWith('/admin/mock-user-selection') ||
        targetPath.startsWith('/line/callback') ||
        targetPath.startsWith('/sample')) {
      next()
      return
    }

    // 檢查用戶是否已登入
    const user = authService.getCurrentUser()
    
    // 未登入用戶導向登入頁面
    if (!user) {
      // 管理後台路由導向管理後台登入頁
      if (targetPath.startsWith('/admin')) {
        next('/admin/login')
        return
      }
      
      // 其他路由導向一般登入頁面
      next('/login')
      return
    }

    // 已登入用戶的權限檢查
    if (targetPath.startsWith('/admin')) {
      // 管理後台路由需要額外的權限檢查
      if (!authService.canAccessRoute(targetPath)) {
        const userRole = user.currentRole?.role || user.role || 'unknown'
        logger.warn(`使用者 ${userRole} 嘗試訪問無權限路由: ${targetPath}`)
        
        // 導向使用者的預設首頁
        const defaultRoute = authService.getDefaultRoute()
        next(defaultRoute)
        return
      }
    }

    // 有權限或為一般用戶路由，允許訪問
    next()
  })

  // 路由錯誤處理
  router.onError((error) => {
    logger.error('路由錯誤', error)
    
    // 如果是權限相關錯誤，導向登入頁
    if (error.message.includes('權限') || error.message.includes('permission')) {
      router.push('/login')
    }
  })
}

/**
 * 店家功能路由守衛
 * 檢查用戶是否有店家權限且已選擇店家身份
 */
export function vendorRouteGuard(to: any, from: any, next: any) {
  // 動態導入避免循環引用
  import('@/services/authService').then(({ authService }) => {
    const user = authService.getCurrentUser()
    
    // 未登入用戶導向登入頁面
    if (!user) {
      next('/login')
      return
    }

    // 檢查是否有店家權限
    if (!authService.hasVendorPermission()) {
      logger.warn(`使用者 ${user.currentRole?.role || 'unknown'} 嘗試訪問店家功能: ${to.path}`)
      
      // 導向個人資料頁面，讓用戶了解需要店家權限
      next('/user/profile')
      return
    }

    // 檢查是否已選擇店家身份
    if (!authService.hasSelectedVendor()) {
      logger.warn(`使用者 ${user.currentRole?.role || 'unknown'} 未選擇店家身份，嘗試訪問: ${to.path}`)
      
      // 導向個人資料頁面，讓用戶選擇店家身份
      next('/user/profile')
      return
    }

    // 有完整店家權限，允許訪問
    next()
  })
}

/**
 * 檢查路由是否需要特定權限
 * 可用於組件內的權限檢查
 */
export function useRoutePermission() {
  const checkCurrentRoute = (): boolean => {
    const currentPath = window.location.pathname
    return authService.canAccessRoute(currentPath)
  }

  const getUserAllowedRoutes = (): string[] => {
    return authService.getUserAllowedRoutes()
  }

  const getDefaultRoute = (): string => {
    return authService.getDefaultRoute()
  }

  return {
    checkCurrentRoute,
    getUserAllowedRoutes,
    getDefaultRoute
  }
}